import React, { useState } from "react";
import ReactDOM from "react-dom";
import { X, Star } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { SECTION_TYPES, SECTION_MAP, favToExercise } from "./constants";

// Portal-rendered slide-in drawer of starred exercises. Search, section-filter,
// preview meta, and one-click add to any non-rest day in the current week.
export function FavoritesPalette({ open, onClose, onAddToDay, days, currentWeek, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (sectionFilter !== "all") params.set("section", sectionFilter);
      const { data } = await api.get(`/coach/favorites${params.toString() ? `?${params}` : ""}`);
      setRows(data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  React.useEffect(() => { if (open) load(); }, [open, q, sectionFilter, refreshKey]);

  const remove = async (favId) => {
    await api.delete(`/coach/favorites/${favId}`);
    setRows((r) => r.filter((x) => x.favorite_id !== favId));
    toast.success("Removed from favorites");
  };

  const currentWeekDays = (days || []).filter((d) => d.week === currentWeek && !d.is_rest);

  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="pointer-events-auto fixed inset-y-0 right-0 z-[100] w-[360px] bg-surface-DEFAULT border-l border-white/15 shadow-2xl flex flex-col" data-testid="favorites-palette">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="label-tiny !text-yellow-400">// FAVORITES</div>
          <div className="font-heading text-lg">Exercise Palette</div>
        </div>
        <button onClick={onClose} data-testid="favorites-close" className="text-text-secondary hover:text-white"><X size={16} /></button>
      </div>
      <div className="p-3 border-b border-white/10 space-y-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or tag..." className="bg-surface-elevated border-white/15 h-9 text-sm" data-testid="favorites-search" />
        <div className="flex flex-wrap gap-1" data-testid="favorites-section-filter">
          {[{ id: "all", label: "ALL" }, ...SECTION_TYPES].map((s) => (
            <button key={s.id} onClick={() => setSectionFilter(s.id)} data-testid={`favorites-filter-${s.id}`}
              className={`text-[10px] label-tiny px-2 py-1 border transition ${sectionFilter === s.id ? "!bg-yellow-400 text-slate-900 !border-yellow-400" : "border-white/15 text-white/80 hover:!border-yellow-400"}`}>
              {(s.label || "").toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5" data-testid="favorites-list">
        {loading ? <div className="p-4 text-xs text-text-secondary">Loading...</div> :
          rows.length === 0 ? (
            <div className="p-4 text-center text-xs text-text-secondary">
              No favorites yet. Star any exercise in a session to add it here.
            </div>
          ) : rows.map((f) => (
            <FavoriteRow
              key={f.favorite_id}
              fav={f}
              days={currentWeekDays}
              onAdd={(dayKey) => {
                const [w, d] = dayKey.split("-").map(Number);
                onAddToDay?.(w, d, favToExercise(f));
                toast.success(`Added "${f.name}" to W${w}·D${d}`);
              }}
              onRemove={() => remove(f.favorite_id)}
            />
          ))
        }
      </div>
      <div className="p-2 border-t border-white/10 text-[10px] text-text-secondary text-center">
        {rows.length} favorite{rows.length === 1 ? "" : "s"}
      </div>
    </div>,
    document.body
  );
}

function FavoriteRow({ fav, days, onAdd, onRemove }) {
  const meta = SECTION_MAP[fav.section] || { label: fav.section, color: "#666", icon: Star };
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-white/[0.02] p-2" data-testid={`favorite-${fav.favorite_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <meta.icon size={10} weight="duotone" style={{ color: meta.color }} />
            <span className="text-[9px] label-tiny" style={{ color: meta.color }}>{(meta.label || "").toUpperCase()}</span>
          </div>
          <div className="text-sm font-heading mt-0.5 truncate">{fav.name}</div>
          <div className="text-[10px] text-text-secondary tabular-nums">
            {fav.sets ? `${fav.sets}×${fav.reps || "?"}` : "no prescription"}{fav.rest_sec ? ` · ${fav.rest_sec}s rest` : ""}
          </div>
          {fav.coach_cue && <div className="text-[10px] text-text-secondary italic mt-0.5 truncate">{fav.coach_cue}</div>}
          {fav.tags && <div className="text-[9px] !text-yellow-400 mt-0.5 truncate">{fav.tags}</div>}
        </div>
        <button onClick={onRemove} data-testid={`favorite-${fav.favorite_id}-remove`} className="shrink-0" title="Remove favorite">
          <Star size={12} weight="fill" className="!text-yellow-400 hover:!text-red-500" />
        </button>
      </div>
      <div className="mt-1.5 relative">
        <button onClick={() => setPickerOpen((v) => !v)} data-testid={`favorite-${fav.favorite_id}-add`}
          className="w-full !bg-yellow-400 hover:!bg-yellow-300 text-slate-900 font-bold py-1 text-[10px] label-tiny tracking-wider transition-colors">
          + ADD TO SESSION
        </button>
        {pickerOpen && (
          <div className="absolute right-0 top-full mt-1 z-30 bg-surface-DEFAULT border border-white/20 shadow-xl min-w-[220px] max-h-56 overflow-y-auto" data-testid={`favorite-${fav.favorite_id}-picker`}>
            {days.length === 0 ? (
              <div className="p-2 text-[10px] text-text-secondary">No sessions this week.</div>
            ) : days.map((d) => (
              <button key={`${d.week}-${d.day}`} onClick={() => { onAdd(`${d.week}-${d.day}`); setPickerOpen(false); }}
                data-testid={`favorite-${fav.favorite_id}-add-w${d.week}-d${d.day}`}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-white/5 border-b border-white/5 last:border-0">
                <span className="!text-yellow-400 font-heading">W{d.week}·D{d.day}</span>
                <span className="ml-2 text-text-secondary">{d.name || "(untitled)"}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
